import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import { ChevronDownIcon, GridIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Button, Popover, Select, Subheading } from '@treams/treams-ui';

import useTalentReviewPath from '../hooks/useTalentReviewPath';

const IconWrap = styled(Box)`
	svg {
		display: block;
		width: 32px;
		height: 32px;
	}
`;

export default function TalentReviewGridPicker(props: {
	groupID: string;
	groupName: string;
	selectedTemplateID?: string;
	onSelect: (templateID: string) => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const path = useTalentReviewPath();
	const [gridMenuOpen, setGridMenuOpen] = useState(false);

	const templatesQuery = graphql.useTalentReviewTemplatesQuery({
		variables: { isArchived: false, first: 1000 },
	});

	const options = useMemo(
		() =>
			(
				templatesQuery.data?.talentReviewTemplates.edges.map(
					({ node }) => node as graphql.TalentReviewTemplate,
				) ?? []
			).map((template) => ({
				value: template.id,
				title: localize(template.title).text,
			})),
		[localize, templatesQuery.data?.talentReviewTemplates.edges],
	);

	return (
		<Flex flexDirection="column" alignItems="center">
			<IconWrap mb={2}>
				<GridIcon />
			</IconWrap>
			<Subheading textAlign="center">
				{t('talentReviews.v2.group.chooseGrid', { name: props.groupName })}
			</Subheading>
			<Flex mt={3}>
				<Popover.Target
					visible={gridMenuOpen}
					zIndex={10}
					onDismiss={() => setGridMenuOpen(false)}
					contents={
						<Select.Menu
							options={options}
							selectedValue={props.selectedTemplateID}
							onChange={(value) => {
								if (value) {
									props.onSelect(String(value));
								}
								setGridMenuOpen(false);
							}}
							onClose={() => setGridMenuOpen(false)}
							autofocus
						/>
					}
				>
					<Button
						title={t('talentReviews.v2.group.selectGrid')}
						rightIcon={<ChevronDownIcon />}
						onClick={() => setGridMenuOpen(true)}
					/>
				</Popover.Target>
				<Button
					ml={2}
					type="secondary"
					title={t('talentReviews.v2.group.createGrid')}
					link={path({ newTemplate: true, groupID: props.groupID })}
				/>
			</Flex>
		</Flex>
	);
}
